/* =========================================================================
   "Leave this round?" — the stop-and-resume sheet behind the play HUD's ◀ and
   the account menu's mid-round "Save & leave" item (PLAN-design-round
   2026-08-21 §A.2). The game clock must not run while it is up, and nothing
   behind it may be answered, so the sheet is a tiny STATE MACHINE with three
   callbacks the controller owns: pause (on open), resume (Keep playing /
   Escape / backdrop) and save (Save & leave). The machine is pure and
   unit-tested; `mountLeaveSheet` / `renderLeaveSheet` are the DOM adapter.

   Resume is ROUND-granular (resumeGame.ts): leaving mid-round keeps the bank
   and every round already finished, and the round in progress restarts from
   its first question next time — the copy says exactly that.
   ========================================================================= */
import { fmtCents } from '../engine';

export interface LeaveInfo {
  /** 0-based index of the round in progress. */
  roundIndex: number;
  roundName: string;
  /** Bank at the START of this round — what a resume restores. */
  bankCents: number;
  /** Question number being played (1-based) and the round's count. */
  question: number;
  total: number;
}

export interface LeaveCopy {
  title: string;
  body: string;
  save: string;
  keep: string;
}

/** The sheet's words. Round 1 has nothing to save yet — say so, rather than
    promising a resume that would only ever land on "Start Round". PURE. */
export function leaveCopy(info: LeaveInfo): LeaveCopy {
  const n = info.roundIndex + 1;
  const title = 'Leave this round?';
  const keep = 'Keep playing';
  if (info.roundIndex === 0) {
    return {
      title,
      body: `You're on Q${info.question} of ${info.total} in Round 1 — nothing is banked yet. Leave now and you'll start fresh next time.`,
      save: 'Leave',
      keep,
    };
  }
  return {
    title,
    body:
      `Your ${fmtCents(info.bankCents)} bank and the ${n - 1} round${n - 1 === 1 ? '' : 's'} you've finished are saved. ` +
      `You'll restart Round ${n} · ${info.roundName} from Q1 next time.`,
    save: 'Save & leave',
    keep,
  };
}

/* ------------------------------- state machine ------------------------------- */

export interface LeaveSheetHandlers {
  /** Fired exactly once per open — stop the clock. */
  onPause: () => void;
  /** Fired on Keep playing / Escape / backdrop — restart the clock. */
  onResume: () => void;
  /** Fired on Save & leave — persist, then navigate. */
  onSave: (info: LeaveInfo) => void;
  /** Paint the sheet (open = show with copy; closed = hide). */
  view?: (open: boolean, copy: LeaveCopy | null) => void;
}

export interface LeaveSheetController {
  open(info: LeaveInfo): void;
  keep(): void;
  save(): void;
  isOpen(): boolean;
}

/** A re-entrant-safe open/keep/save machine: a second open while open is a
    no-op (one pause per open), keep/save while closed are no-ops, and save
    closes the sheet BEFORE navigating so a stale "open" can never survive into
    the next screen. PURE apart from the injected handlers. */
export function createLeaveSheet(h: LeaveSheetHandlers): LeaveSheetController {
  let open = false;
  let current: LeaveInfo | null = null;
  const paint = (isOpen: boolean, info: LeaveInfo | null): void => {
    if (h.view) h.view(isOpen, info ? leaveCopy(info) : null);
  };
  return {
    open(info) {
      if (open) return;
      open = true;
      current = info;
      h.onPause();
      paint(true, info);
    },
    keep() {
      if (!open) return;
      open = false;
      current = null;
      paint(false, null);
      h.onResume();
    },
    save() {
      if (!open || !current) return;
      const info = current;
      open = false;
      current = null;
      paint(false, null);
      h.onSave(info);
    },
    isOpen: () => open,
  };
}

/* ------------------------------- DOM adapter ------------------------------- */

export const leaveSheetHTML = /* html */ `
<div id="leaveSheet" class="leave-sheet hidden" role="dialog" aria-modal="true" aria-labelledby="leaveTitle" aria-hidden="true">
  <div class="leave-dim" id="leaveDim"></div>
  <div class="leave-card">
    <div class="leave-ic" aria-hidden="true">⏸</div>
    <div class="leave-title" id="leaveTitle">Leave this round?</div>
    <div class="leave-body" id="leaveBody"></div>
    <button class="btn leave-keep" id="leaveKeep" type="button">Keep playing</button>
    <button class="btn secondary leave-save" id="leaveSave" type="button">Save &amp; leave</button>
  </div>
</div>`;

/** Show/hide + copy. Safe to call before the sheet is mounted (no-op). */
export function renderLeaveSheet(open: boolean, copy: LeaveCopy | null): void {
  const el = document.getElementById('leaveSheet');
  if (!el) return;
  if (open && copy) {
    (document.getElementById('leaveTitle') as HTMLElement).textContent = copy.title;
    (document.getElementById('leaveBody') as HTMLElement).textContent = copy.body;
    (document.getElementById('leaveSave') as HTMLElement).textContent = copy.save;
    (document.getElementById('leaveKeep') as HTMLElement).textContent = copy.keep;
  }
  el.classList.toggle('hidden', !open);
  el.setAttribute('aria-hidden', open ? 'false' : 'true');
  if (open) {
    // Focus the safe choice; the question behind is inert while the dim is up.
    (document.getElementById('leaveKeep') as HTMLButtonElement | null)?.focus();
  } else {
    (document.getElementById('hudBackBtn') as HTMLButtonElement | null)?.focus?.();
  }
}

/** Insert the markup (once) next to the account-menu chrome and wire the
    buttons, backdrop and Escape to the controller. */
export function mountLeaveSheet(ctl: LeaveSheetController, anchorId = 'accountMenu'): void {
  if (document.getElementById('leaveSheet')) return;
  const anchor = document.getElementById(anchorId);
  if (anchor) anchor.insertAdjacentHTML('afterend', leaveSheetHTML);
  else document.body.insertAdjacentHTML('beforeend', leaveSheetHTML);
  document.getElementById('leaveKeep')?.addEventListener('click', () => ctl.keep());
  document.getElementById('leaveSave')?.addEventListener('click', () => ctl.save());
  document.getElementById('leaveDim')?.addEventListener('click', () => ctl.keep());
  // Escape = keep playing. Enter/Space on a focused sheet button activates it
  // here because main's global keydown preventDefault()s native activation.
  document.addEventListener('keydown', (e) => {
    if (!ctl.isOpen()) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      ctl.keep();
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      const t = e.target as HTMLElement | null;
      if (t && (t.id === 'leaveKeep' || t.id === 'leaveSave')) {
        e.preventDefault();
        t.click();
      }
    }
  });
}
